import React from 'react';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import Select from 'react-select';
import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

const SearchHero = ({
  selectedState,
  selectedCity,
  searchType,
  stateOptions,
  cityOptions,
  onStateChange,
  onCityChange,
  onTypeChange,
  onSearch,
  loading,
}) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: 48,
      borderRadius: 8,
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  return (
    <Box>
      <Box marginBottom={4}>
        <Typography
          variant="h3"
          color="text.primary"
          align="center"
          sx={{
            fontWeight: 700,
            marginBottom: 2,
          }}
        >Find{' '}
          <Typography
            component="span"
            variant="inherit"
            color="primary"
          >
Driving Schools
          </Typography>{' '}
            &{' '}
          <Typography
            component="span"
            variant="inherit"
            color="primary"
          >
            Car Dealers
          </Typography>{' '}
          Near You
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          align="center"
          sx={{ fontWeight: 400 }}
        >
          Search by location to discover the best options in your city
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          borderRadius: 2,
          padding: { xs: 3, md: 4 },
          boxShadow: theme.shadows[4],
        }}
      >
        {/* Search Type Toggle */}
        <Box
          display="flex"
          justifyContent="center"
          marginBottom={3}
        >
          <ToggleButtonGroup
            value={searchType}
            exclusive
            onChange={(e, value) => value && onTypeChange(value)}
            aria-label="search type"
            sx={{
              '& .MuiToggleButton-root': {
                px: { xs: 2, md: 4 },
                py: 1,
              },
            }}
          >
            <ToggleButton value="organizations" aria-label="driving schools">
              <SchoolIcon sx={{ mr: 1 }} />
              Driving Schools
            </ToggleButton>
            <ToggleButton value="dealers" aria-label="car dealers">
              <DirectionsCarIcon sx={{ mr: 1 }} />
              Car Dealers
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Location Filters */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
        >
          {/* State Dropdown */}
          <Box flex={1}>
            <Typography variant="body2" color="text.secondary" marginBottom={1}>
              State
            </Typography>
            <FormControl fullWidth>
              <Select
                styles={selectStyles}
                placeholder="Select State"
                value={selectedState}
                options={stateOptions}
                onChange={onStateChange}
                isClearable
              />
            </FormControl>
          </Box>

          {/* City Dropdown */}
          <Box flex={1}>
            <Typography variant="body2" color="text.secondary" marginBottom={1}>
              City
            </Typography>
            <FormControl fullWidth>
              <Select
                styles={selectStyles}
                placeholder={selectedState ? "Select City" : "Select State first"}
                value={selectedCity}
                options={cityOptions}
                onChange={onCityChange}
                isDisabled={!selectedState}
                isClearable
              />
            </FormControl>
          </Box>

          {/* Search Button */}
          <Box>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={onSearch}
              disabled={loading || !selectedState}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              sx={{
                height: 48,
                px: 4,
                borderRadius: 2,
                minWidth: { xs: '100%', md: 150 },
              }}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

SearchHero.propTypes = {
  selectedState: PropTypes.object,
  selectedCity: PropTypes.object,
  searchType: PropTypes.oneOf(['organizations', 'dealers']),
  stateOptions: PropTypes.array.isRequired,
  cityOptions: PropTypes.array.isRequired,
  onStateChange: PropTypes.func.isRequired,
  onCityChange: PropTypes.func.isRequired,
  onTypeChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

SearchHero.defaultProps = {
  selectedState: null,
  selectedCity: null,
  searchType: 'organizations',
  loading: false,
};

export default SearchHero;
